import { useEffect, useMemo, useState } from "react";
import { getContactsByIds } from "../utils/firebasefunctions";

// Pulls a display name off a contact/customer record, whatever shape it's in.
// sync_contacts docs vary: app-saved ones carry a computed `name`, but legacy /
// imported ones may only have firstName/lastName (no top-level `name`), and
// inline deal-customer objects use `clientName`. Check them all so no contact
// resolves to blank.
function pickName(record) {
  if (!record) return "";
  const direct = record.name || record.clientName || record.fullName;
  if (direct && direct.trim()) return direct.trim();
  const composed = [record.firstName, record.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return composed;
}

// A deal's `customers` comes in two shapes:
//   - modern: ["contactId", ...]  (looked up in sync_contacts)
//   - legacy: [{ clientName, name?, ... }, ...]
// Resolves one entry to a display name, given a map of fetched contacts.
function nameForEntry(entry, contactsById) {
  if (!entry) return "";
  if (typeof entry === "object") return pickName(entry);
  return pickName(contactsById[entry]);
}

// Joins all of a deal's client names with " & ". Returns "" when none resolve.
export function buildClientName(deal, contactsById) {
  return (deal?.customers || [])
    .map((c) => nameForEntry(c, contactsById))
    .filter(Boolean)
    .join(" & ");
}

// Fetches the contacts referenced by `deals` (modern ID shape only) and returns
// a lookup map plus a ready-to-render name getter (falls back to "—").
export function useDealClientNames(deals) {
  const [contactsById, setContactsById] = useState({});

  const idKey = useMemo(() => {
    const ids = Array.from(
      new Set(
        (deals || [])
          .flatMap((d) => d?.customers || [])
          // Only real, non-empty string IDs. Deals can carry empty client
          // slots (clientinfo seeds as [""]) — an empty ID makes Firestore
          // reject the whole batch, so drop them here.
          .filter((c) => typeof c === "string" && c.trim() !== ""),
      ),
    ).sort();
    return ids.join(",");
  }, [deals]);

  useEffect(() => {
    const ids = idKey ? idKey.split(",") : [];
    if (!ids.length) return;
    let cancelled = false;

    getContactsByIds(ids)
      .then((contacts) => {
        if (cancelled) return;
        const map = {};
        for (const c of contacts) {
          if (c?.id) map[c.id] = c;
          if (c?.firebaseId) map[c.firebaseId] = c;
        }
        setContactsById((prev) => ({ ...prev, ...map }));
      })
      .catch((err) => console.error("Failed to resolve client names:", err));

    return () => {
      cancelled = true;
    };
  }, [idKey]);

  return {
    contactsById,
    getClientName: (deal) => buildClientName(deal, contactsById) || "—",
  };
}
